import { z } from 'zod';
import {
    CloverRefundOutcome,
    CloverRefundPaymentResult,
    CloverRefundKind,
    type CloverRefundBody,
    type CloverRefundAdditionalChargeBody,
    type CloverRefundAdditionalChargesBody,
    type CloverRefundAmounts,
    type CloverRefundCardTransactionBody,
    type CloverRefundErrorBody,
    type CloverRefundMalformedResponseDetails,
    type CloverRefundPaymentBody,
    type CloverRefundReferenceBody,
    type CloverRefundRejectedResponseDetails,
    type CloverCloudPayRefundRequestBody,
    type CloverCloudPayRefundRequestDetails,
    type CloverCloudPayRefundSuccessfulResponseDetails,
    type CloverCloudPayRefundRequestHeaders,
    type CloverEcommerceRefundRequestDetails,
    type CloverEcommerceRefundResponseBody,
    type CloverEcommerceRefundSuccessfulResponseDetails,
    type CloverRefundRequestDetails,
    type CloverRefundResponseBody,
    type CloverRefundResponseDetails,
    type CloverRefundSuccessfulResponseDetails,
} from '../../../types/ipg/clover/clover-refund.types';

export const CloverRefundPaymentResultSchema: z.ZodType<CloverRefundPaymentResult> =
    z.enum(CloverRefundPaymentResult);

export const CloverRefundAmountSchema: z.ZodNumber = z.number().int().min(1).max(999999999);

export const CloverCloudPayRefundRequestHeadersSchema: z.ZodObject<{
    deviceId: z.ZodString;
    idempotencyKey: z.ZodString;
    posId: z.ZodString;
    userAgent: z.ZodString;
    timeoutSeconds: z.ZodOptional<z.ZodNumber>;
}> = z.object({
    deviceId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    posId: z.string().min(1),
    userAgent: z.string().min(1),
    timeoutSeconds: z.number().int().min(0).max(300).optional(),
}) satisfies z.ZodType<CloverCloudPayRefundRequestHeaders>;

export const CloverCloudPayRefundRequestBodySchema: z.ZodObject<{
    amount: z.ZodOptional<typeof CloverRefundAmountSchema>;
    fullRefund: z.ZodOptional<z.ZodBoolean>;
}> = z.object({
    amount: CloverRefundAmountSchema.optional(),
    fullRefund: z.boolean().optional(),
}) satisfies z.ZodType<CloverCloudPayRefundRequestBody>;

export const CloverCloudPayRefundRequestDetailsSchema: z.ZodObject<{
    kind: z.ZodLiteral<typeof CloverRefundKind.CLOUD_PAY>;
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/payments/{paymentId}/refunds'>;
    paymentId: z.ZodString;
    headers: typeof CloverCloudPayRefundRequestHeadersSchema;
    body: typeof CloverCloudPayRefundRequestBodySchema;
}> = z.object({
    kind: z.literal(CloverRefundKind.CLOUD_PAY),
    method: z.literal('POST'),
    path: z.literal('/v1/payments/{paymentId}/refunds'),
    paymentId: z.string().min(1),
    headers: CloverCloudPayRefundRequestHeadersSchema,
    body: CloverCloudPayRefundRequestBodySchema,
}) satisfies z.ZodType<CloverCloudPayRefundRequestDetails>;

export const CloverEcommerceRefundRequestDetailsSchema = z
    .object({
        kind: z.literal(CloverRefundKind.ECOMMERCE),
        method: z.literal('POST'),
        path: z.literal('/v1/refunds'),
        chargeId: z.string().min(1),
        headers: z
            .object({
                idempotencyKey: z.string().min(1),
                userAgent: z.string().min(1),
            })
            .strict(),
        body: z
            .object({
                charge: z.string().min(1),
                amount: CloverRefundAmountSchema.optional(),
            })
            .strict(),
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundRequestDetails>;

export const CloverRefundRequestDetailsSchema: z.ZodType<CloverRefundRequestDetails> =
    z.discriminatedUnion('kind', [
        CloverCloudPayRefundRequestDetailsSchema,
        CloverEcommerceRefundRequestDetailsSchema,
    ]);

export const CloverRefundReferenceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundReferenceBody>;

export const CloverRefundAdditionalChargeBodySchema = z
    .object({
        id: z.string().min(1).optional(),
        amount: z.number().int().min(0).max(999999999),
        rate: z.number().int().min(0).optional(),
        type: z.literal('CREDIT_SURCHARGE'),
        refund: z
            .object({
                id: z.string().min(1),
            })
            .strict()
            .optional(),
    })
    .strict() satisfies z.ZodType<CloverRefundAdditionalChargeBody>;

export const CloverRefundAdditionalChargesBodySchema = z
    .object({
        elements: z.array(CloverRefundAdditionalChargeBodySchema),
    })
    .strict() satisfies z.ZodType<CloverRefundAdditionalChargesBody>;

export const CloverRefundAmountsSchema: z.ZodType<CloverRefundAmounts> = z
    .object({
        underlyingAmountMinorUnits: z.number().int().min(1).max(999999999),
        creditSurchargeAmountMinorUnits: z.number().int().min(0).max(999999999),
        totalAmountMinorUnits: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    })
    .strict()
    .superRefine((amounts, context) => {
        if (
            amounts.totalAmountMinorUnits !==
            amounts.underlyingAmountMinorUnits + amounts.creditSurchargeAmountMinorUnits
        ) {
            context.addIssue({
                code: 'custom',
                path: ['totalAmountMinorUnits'],
                message: 'Total refund amount must equal underlying amount plus credit surcharge',
            });
        }
    });

export const CloverRefundCardTransactionBodySchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundCardTransactionBody>;

export const CloverRefundBodySchema: z.ZodType<CloverRefundBody> = z.object({
    id: z.string().min(1).optional(),
    amount: CloverRefundAmountSchema.optional(),
    cardTransaction: CloverRefundCardTransactionBodySchema.optional(),
    createdTime: z.number().int().min(0).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    payment: CloverRefundReferenceBodySchema.optional(),
    taxAmount: z.number().int().min(0).optional(),
    tipAmount: z.number().int().min(0).optional(),
    voided: z.boolean().optional(),
    additionalCharges: CloverRefundAdditionalChargesBodySchema.optional(),
});

export const CloverRefundPaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<typeof CloverRefundAmountSchema>;
    result: z.ZodOptional<typeof CloverRefundPaymentResultSchema>;
    cardTransaction: z.ZodOptional<typeof CloverRefundCardTransactionBodySchema>;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
    refunds: z.ZodOptional<z.ZodArray<typeof CloverRefundBodySchema>>;
}> = z.object({
    id: z.string().min(1).max(13).optional(),
    amount: CloverRefundAmountSchema.optional(),
    result: CloverRefundPaymentResultSchema.optional(),
    cardTransaction: CloverRefundCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).max(32).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
    refunds: z.array(CloverRefundBodySchema).optional(),
}) satisfies z.ZodType<CloverRefundPaymentBody>;

export const CloverRefundResponseBodySchema: z.ZodObject<{
    payment: z.ZodOptional<typeof CloverRefundPaymentBodySchema>;
    paymentId: z.ZodString;
    refund: z.ZodOptional<typeof CloverRefundBodySchema>;
}> = z.object({
    payment: CloverRefundPaymentBodySchema.optional(),
    paymentId: z.string().min(1),
    refund: CloverRefundBodySchema.optional(),
}) satisfies z.ZodType<CloverRefundResponseBody>;

export const CloverRefundErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverRefundErrorBody>;

export const CloverCloudPayRefundSuccessfulResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodLiteral<typeof CloverRefundKind.CLOUD_PAY>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.REFUNDED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverRefundResponseBodySchema;
    refundAmounts: z.ZodOptional<typeof CloverRefundAmountsSchema>;
}> = z.object({
    kind: z.literal(CloverRefundKind.CLOUD_PAY),
    outcome: z.literal(CloverRefundOutcome.REFUNDED),
    httpStatus: z.literal(200),
    body: CloverRefundResponseBodySchema,
    refundAmounts: CloverRefundAmountsSchema.optional(),
}) satisfies z.ZodType<CloverCloudPayRefundSuccessfulResponseDetails>;

export const CloverEcommerceRefundResponseBodySchema = z
    .object({
        id: z.string().min(1),
        object: z.literal('refund'),
        amount: CloverRefundAmountSchema,
        charge: z.string().min(1),
        created: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
        status: z.literal('succeeded'),
        additional_charges: CloverRefundAdditionalChargeBodySchema.optional(),
        metadata: z
            .object({
                refundType: z.string().min(1).optional(),
                authCode: z.string().min(1).optional(),
                refNum: z.string().min(1).optional(),
                gatewayResponseCode: z.string().min(1).optional(),
            })
            .strict()
            .optional(),
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundResponseBody>;

export const CloverEcommerceRefundSuccessfulResponseDetailsSchema = z
    .object({
        kind: z.literal(CloverRefundKind.ECOMMERCE),
        outcome: z.literal(CloverRefundOutcome.REFUNDED),
        httpStatus: z.literal(200),
        body: CloverEcommerceRefundResponseBodySchema,
        refundAmounts: CloverRefundAmountsSchema.optional(),
    })
    .strict() satisfies z.ZodType<CloverEcommerceRefundSuccessfulResponseDetails>;

export const CloverRefundSuccessfulResponseDetailsSchema: z.ZodType<CloverRefundSuccessfulResponseDetails> =
    z.discriminatedUnion('kind', [
        CloverCloudPayRefundSuccessfulResponseDetailsSchema,
        CloverEcommerceRefundSuccessfulResponseDetailsSchema,
    ]);

export const CloverRefundRejectedResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodEnum<typeof CloverRefundKind>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.REJECTED>;
    httpStatus: z.ZodNumber;
    body: typeof CloverRefundErrorBodySchema;
}> = z.object({
    kind: z.enum(CloverRefundKind),
    outcome: z.literal(CloverRefundOutcome.REJECTED),
    httpStatus: z.number().int().positive(),
    body: CloverRefundErrorBodySchema,
}) satisfies z.ZodType<CloverRefundRejectedResponseDetails>;

export const CloverRefundMalformedResponseDetailsSchema: z.ZodObject<{
    kind: z.ZodEnum<typeof CloverRefundKind>;
    outcome: z.ZodLiteral<typeof CloverRefundOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    kind: z.enum(CloverRefundKind),
    outcome: z.literal(CloverRefundOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CloverRefundMalformedResponseDetails>;

export const CloverRefundResponseDetailsSchema: z.ZodType<CloverRefundResponseDetails> = z.union([
    CloverCloudPayRefundSuccessfulResponseDetailsSchema,
    CloverEcommerceRefundSuccessfulResponseDetailsSchema,
    CloverRefundRejectedResponseDetailsSchema,
    CloverRefundMalformedResponseDetailsSchema,
]);
